/**
 * LGL Portal scan capture — barcode (BarcodeDetector) + OCR label text (Tesseract).
 * Camera stays on while hunting, then turns off so matches can be picked on a light screen.
 */

import React, { useEffect, useRef, useState } from 'react';
import { MOBILE_APP_KICKER } from './appBranding';
import { BrandMark } from './LglMark';
import { performOcr } from '../../utils/localOcr';
import {
  extractCandidateLabels,
  matchScanToRows,
  parseBarcodeValue,
} from '../../utils/trackerBarcode';

const stopStream = (stream) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => track.stop());
};

export const ScanCapture = ({
  open,
  discipline,
  rows,
  onClose,
  onMatch,
}) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [mode, setMode] = useState('barcode'); // barcode | ocr
  const [cameraOn, setCameraOn] = useState(true);
  const [status, setStatus] = useState('Point at a barcode or label');
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState('');

  const showResults = candidates.length > 0;

  useEffect(() => {
    if (!open) {
      setCameraOn(true);
      setCandidates([]);
      setError('');
      setManual('');
      return undefined;
    }
    if (!cameraOn) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      stopStream(streamRef.current);
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      return undefined;
    }

    let cancelled = false;
    const start = async () => {
      setError('');
      setCandidates([]);
      setStatus(mode === 'barcode' ? 'Looking for a barcode…' : 'Hold steady, then tap Read label');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stopStream(stream);
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        setError(err.message || 'Camera permission is required to scan');
      }
    };

    start();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      stopStream(streamRef.current);
      streamRef.current = null;
    };
  }, [cameraOn, mode, open]);

  useEffect(() => {
    if (!open || !cameraOn || mode !== 'barcode') return undefined;
    if (typeof window === 'undefined' || typeof window.BarcodeDetector !== 'function') {
      setStatus('This phone has no barcode reader — use Read label or type the ISO / tag');
      return undefined;
    }

    let alive = true;
    const detector = new window.BarcodeDetector({
      formats: ['code_128', 'code_39', 'qr_code', 'ean_13', 'ean_8', 'upc_a', 'upc_e'],
    });

    const tick = async () => {
      if (!alive || !videoRef.current) return;
      try {
        const codes = await detector.detect(videoRef.current);
        const value = String(codes?.[0]?.rawValue || '').trim();
        if (value) {
          handleRaw(value, 'barcode');
          return;
        }
      } catch {
        // keep scanning
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      alive = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cameraOn, mode, open, rows, discipline]);

  const handleRaw = (raw, source) => {
    const matches = matchScanToRows(raw, rows, discipline?.id);
    if (matches.length === 1 || (matches[0] && matches[0].score >= 90)) {
      setStatus('Match found — opening line');
      onMatch(matches[0].row, { raw, source, parsed: parseBarcodeValue(raw) });
      return;
    }
    if (matches.length > 1) {
      setCandidates(matches.slice(0, 8).map((item) => ({
        id: item.row.id,
        title: item.row,
        score: item.score,
        raw,
      })));
      setStatus(`Found ${matches.length} possible matches — pick one`);
      setCameraOn(false);
      return;
    }
    setStatus(`No match for “${raw}” in this tracker`);
    setCandidates([]);
  };

  const readLabel = async () => {
    if (!videoRef.current) return;
    setBusy(true);
    setError('');
    setStatus('Reading label text…');
    try {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const result = await performOcr(canvas);
      if (!result.success) {
        setError(result.error || 'OCR failed');
        setStatus('Could not read the label');
        return;
      }
      const labels = extractCandidateLabels(result.text);
      let best = null;
      for (const label of labels) {
        const matches = matchScanToRows(label, rows, discipline?.id);
        if (matches[0] && (!best || matches[0].score > best.score)) {
          best = { ...matches[0], raw: label };
        }
      }
      if (best && best.score >= 70) {
        if (best.score >= 90) {
          onMatch(best.row, { raw: best.raw, source: 'ocr', parsed: parseBarcodeValue(best.raw) });
          return;
        }
        const all = matchScanToRows(best.raw, rows, discipline?.id);
        setCandidates(all.slice(0, 8).map((item) => ({
          id: item.row.id,
          title: item.row,
          score: item.score,
          raw: best.raw,
        })));
        setStatus('Pick the matching ISO / tag');
        setCameraOn(false);
        return;
      }
      setStatus(labels[0] ? `Read “${labels[0]}” but no tracker match` : 'No text found on the label');
    } catch (err) {
      setError(err.message || 'OCR failed');
    } finally {
      setBusy(false);
    }
  };

  const scanAgain = () => {
    setCandidates([]);
    setError('');
    setCameraOn(true);
  };

  if (!open) return null;

  return (
    <div className={`lgl-m-scan${showResults ? ' is-results' : ''}`}>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <button type="button" className="lgl-m-back" onClick={onClose} aria-label="Close scan">←</button>
          <div>
            <p className="lgl-m-kicker">{MOBILE_APP_KICKER}</p>
            <div className="lgl-m-jobname">Scan</div>
            <div className="lgl-m-sub">{discipline?.name || 'Tracker'}</div>
          </div>
          <BrandMark />
        </div>
      </header>

      <div className="lgl-m-scanbody">
        <div className="lgl-m-scanmodes">
          <button
            type="button"
            className={`lgl-m-chip${mode === 'barcode' ? ' is-on' : ''}`}
            onClick={() => {
              setMode('barcode');
              setCandidates([]);
              setCameraOn(true);
            }}
          >
            Barcode
          </button>
          <button
            type="button"
            className={`lgl-m-chip${mode === 'ocr' ? ' is-on' : ''}`}
            onClick={() => {
              setMode('ocr');
              setCandidates([]);
              setCameraOn(true);
            }}
          >
            Read label
          </button>
        </div>

        {cameraOn ? (
          <div className="lgl-m-scanview">
            <video ref={videoRef} playsInline muted autoPlay className="lgl-m-scanvideo" />
            <div className="lgl-m-scanframe" />
          </div>
        ) : (
          <p className="lgl-m-scanoff">Camera is off so you can pick a match.</p>
        )}

        <p className="lgl-m-scanstatus">{status}</p>
        {error ? <p className="lgl-m-error">{error}</p> : null}

        {mode === 'ocr' && cameraOn ? (
          <button
            type="button"
            className="lgl-m-primary"
            disabled={busy}
            onClick={readLabel}
          >
            {busy ? 'Reading…' : 'Read label now'}
          </button>
        ) : null}

        {!cameraOn ? (
          <button type="button" className="lgl-m-chipbtn" onClick={scanAgain}>
            Scan again
          </button>
        ) : null}

        <form
          className="lgl-m-scanmanual"
          onSubmit={(e) => {
            e.preventDefault();
            if (!manual.trim()) return;
            handleRaw(manual.trim(), 'manual');
          }}
        >
          <input
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            placeholder="Or type ISO / tag / barcode"
            enterKeyHint="search"
          />
          <button type="submit">Find</button>
        </form>

        {candidates.length ? (
          <div className="lgl-m-scanhits" role="list" aria-label="Scan matches">
            {candidates.map((item) => {
              const title = item.title?.isoSheetNumber
                || item.title?.equipmentTagOrCode
                || `Item ${item.id}`;
              return (
                <button
                  key={item.id}
                  type="button"
                  className="lgl-m-card"
                  role="listitem"
                  onClick={() => onMatch(item.title, { raw: item.raw, source: 'pick' })}
                >
                  <div className="lgl-m-jobname">{title}</div>
                  <div className="lgl-m-sub">Match score {item.score}</div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ScanCapture;
